import { Box, Chip, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../DashboardContext';
import DashboardWidget, { useDashboardWidgetScrollHeight } from './DashboardWidget';

const SupplierPayablesOverview = () => {
  const { topSupplierBalances, overdueSupplies, kpi, sectionLoading, currency } = useDashboard();
  const { t } = useTranslation();
  const scrollHeight = useDashboardWidgetScrollHeight(280);

  const formatAmount = (value: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value) + ` ${currency}`;

  if (sectionLoading.activity) {
    return (
      <DashboardWidget
        title={t('common:dashboard.supplierPayables.title')}
        helpKey="supplierPayables.title"
        chartHeight={320}
        scrollable
      >
        <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 2 }} />
      </DashboardWidget>
    );
  }

  const hasBalances = topSupplierBalances.length > 0;
  const hasOverdue = overdueSupplies.length > 0;

  return (
    <DashboardWidget
      title={t('common:dashboard.supplierPayables.title')}
      helpKey="supplierPayables.title"
      subtitle={t('common:dashboard.supplierPayables.subtitle', {
        owed: formatAmount(kpi.supplierBalanceOwed),
        overdue: kpi.overdueSupplyCount,
      })}
      chartHeight={320}
      scrollable
    >
      {!hasBalances && !hasOverdue ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: 'text.secondary' }}>
          <Typography>{t('common:dashboard.supplierPayables.noBalances')}</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: scrollHeight }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('common:dashboard.supplierPayables.supplier')}</TableCell>
                <TableCell align="right">{t('common:dashboard.supplierPayables.balance')}</TableCell>
                <TableCell>{t('common:dashboard.supplierPayables.dueDate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topSupplierBalances.map((row) => (
                <TableRow key={`balance-${row.supplier}`} hover>
                  <TableCell>{row.supplier}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatAmount(row.balance)}
                  </TableCell>
                  <TableCell>—</TableCell>
                </TableRow>
              ))}
              {overdueSupplies.map((row) => (
                <TableRow key={`overdue-${row.batch}`} hover sx={{ bgcolor: 'error.light', '&:hover': { bgcolor: 'error.light' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{row.supplier}</Typography>
                      <Chip label={row.batch} size="small" variant="outlined" />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatAmount(row.balance)}
                  </TableCell>
                  <TableCell>{row.dueDate || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DashboardWidget>
  );
};

export default SupplierPayablesOverview;